import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RenderRowData from './RenderRowData';

const useLocalStyles = makeStyles((theme) => ({
    // Table container + table
    tableContainer: {
        width: '100%',
    },
    table: {
        minWidth: 650,
    },

    // Head
    tableHead: {
        backgroundColor: 'rgba(250, 250, 254, 1)',
    },
    stickyHead: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    headCell: {
        color: 'rgba(69, 70, 83, 1)',
        fontWeight: 600,
        fontSize: 14,
        fontStyle: 'normal',
        [theme.breakpoints.down('sm')]: {
            fontSize: 13,
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: 12,
        },
    },

    // Body
    tableBody: {
        backgroundColor: '#fff',
    },
    stripedBody: {
        '& tr:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
        },
    },

    // Row tweaks
    //   dataRow: {
    //     // keep it clean; padding is defined on cells typically
    //   },

    dataRow: {
        '& > .MuiTableCell-root': {
            padding: '5px 8px', // row cell padding
            [theme.breakpoints.down('sm')]: {
                padding: '4px 6px',
            },
            [theme.breakpoints.down('xs')]: {
                padding: '3px 4px',
            },
        },
    },

    // Loader row
    loaderCell: {
        height: 200,
        padding: 0,
    },
    loaderBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },

    // Empty state
    emptyCell: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
}));

// Your chip styles retained
const useChipStyles = makeStyles({
    customChip: {
        borderRadius: '24px',
        height: '24px',
        padding: '5px 0px 5px',
        border: '1px solid  rgba(25, 118, 210, 1)',
        color: 'rgba(25, 118, 210, 1)',
        maxWidth: '125px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    customChip1: {
        borderRadius: '24px',
        height: '24px',
        padding: '5px 0px 5px',
        border: '1px solid  rgba(239, 108, 0, 1)',
        color: 'rgba(239, 108, 0, 1)',
        maxWidth: '100px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    customChip2: {
        borderRadius: '24px',
        height: '24px',
        padding: '5px 0px 5px',
        border: '1px solid  rgba(46, 125, 50, 1)',
        color: 'rgba(46, 125, 50, 1)',
        maxWidth: '100px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },

    menuPaper: {
        width: 110,
        padding: 2,
        borderRadius: 4,
        background: '#FFF',
        boxShadow: '0 4px 14.2px 0 rgba(0, 0, 0, 0.25)',
    },
    menuItem: {
        fontSize: 14,
        minHeight: '32px',
        padding: '4px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
});

const SubDataModelTable = (props) => {
    const {
        stickyHeader,
        stripedTable,
        enableActions,
        tableHeaders,
        tableRows,
        isLoading,
        loader,
        onTableActionClick,
    } = props;

    const classes = useLocalStyles();
    const chipClasses = useChipStyles();

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [activeRowIndex, setActiveRowIndex] = useState(null);

    const handleMenuOpen = (event, rowIndex) => {
        setMenuAnchorEl(event.currentTarget);
        setActiveRowIndex(rowIndex);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setActiveRowIndex(null);
    };

    return (
        <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} aria-label="sub data model table">
                <TableHead
                    className={`${classes.tableHead} ${stickyHeader ? classes.stickyHead : ''}`}
                >
                    <TableRow>
                        {tableHeaders?.map((header, lIndex) => (
                            <TableCell key={lIndex} align="left" className={classes.headCell}>
                                {header.label || header.description}
                            </TableCell>
                        ))}
                        {enableActions && (
                            <TableCell key="actionsColumn" align="left" className={classes.headCell}>
                                Action
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody
                    className={`${classes.tableBody} ${stripedTable ? classes.stripedBody : ''}`}
                >
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={(tableHeaders?.length || 0) + (enableActions ? 1 : 0)} className={classes.loaderCell}>
                                <div className={classes.loaderBox}>{loader}</div>
                            </TableCell>
                        </TableRow>
                    ) : tableRows?.length > 0 ? (
                        <>
                            {tableRows.map((row, rIndex) => (
                                <TableRow key={rIndex} className={classes.dataRow}>
                                    <RenderRowData
                                        {...props}
                                        row={row}
                                        rIndex={rIndex}
                                        classess={chipClasses}
                                        anchorElDrop={activeRowIndex === rIndex ? menuAnchorEl : null}
                                        handleClick={(e, index) => handleMenuOpen(e, index)}
                                        handleClose={handleMenuClose}
                                        onTableActionClick={(type, r) => {
                                            handleMenuClose();
                                            onTableActionClick(type, r);
                                        }}
                                    />
                                </TableRow>
                            ))}
                        </>
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={(tableHeaders?.length || 0) + (enableActions ? 1 : 0)}
                                className={classes.emptyCell}
                            >
                                <Typography align="center">No Actions</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default SubDataModelTable;
